import React, { useState, useContext } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import ReactDatePicker from "react-datepicker";
import { FormGroup, FormControl, Figure } from "react-bootstrap";
import { BsClockFill, BsFillCalendarEventFill } from "react-icons/bs";
import { IoMdOpen } from "react-icons/io";
import FieldError from "../../../shared/FieldError";
import { Settings } from "../../../helpers/Contexts";
import * as Utils from "../../../helpers/Utility";
import SelectPcp from "../../forms/SelectPcp";

const AppointmentDateSchema = Yup.object().shape({
  appointment_time: Yup.string()
    .matches(
      "([01]?[0-9]|2[0-3]):[0-5][0-9]",
      "Time value must be in valid hh:mm military or 12 hour format. Include AM/PM if using 12 hour format. "
    )
    .required("Appointment time is required"),
  appointment_date: Yup.date().required("Appointment date is required"),
  pcp: Yup.string().nullable(),
});

const DueDateSchema = Yup.object().shape({
  due_date: Yup.date().required("Due date is required."),
  pcp: Yup.string().nullable(),
});

const ExtendedForm = React.memo(({ data }) => {
  React.useEffect(() => {}, [data.selectedDateType]);
  const settings = useContext(Settings);
  return (
    <Formik
      validationSchema={
        data.selectedDateType == "appointment_date" ? AppointmentDateSchema : DueDateSchema
      }
      validateOnChange={true}
      initialValues={
        data.selectedDateType == "appointment_date"
          ? {
              date: data.appointment_date ? data.appointment_date : data.due_date,
              appointment_date: data.appointment_date ? data.appointment_date : data.due_date,
              appointment_time: data.appointment_time
                ? new Date(data.appointment_time).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "",
              order_document: "",
              pcp: data.servicing_provider ? data.servicing_provider : "",
            }
          : {
              due_date: data.due_date ? data.due_date : data.appointment_date,
              date: data.due_date ? data.due_date : data.appointment_date,
              order_document:  "",
              pcp: data.servicing_provider ? data.servicing_provider : "",
            }
      }
      onSubmit={(values) => {
        let params = {
          task: {
            id: data.id,
            status: "closed",
            servicing_provider: values.pcp,
            extended: true,
            in_review: true,
            priority: null,
            assigner_id: settings.me.id,
            response_attributes: {
              id: data.responseId,
              outcome: "cpr_still_required",
            },
          },
        }

        if (values.order_document) {

          // we still have to verify the document if it has been uploaded.
          params['task']['status'] = 'open';
          const formData = new FormData();
          formData.append("task[id]", data.id);
          formData.append("task[order_document]", values.order_document);
          data.formHandler(formData);
        }

        if (data.selectedDateType == "appointment_date") {
          params['task']['appointment_date'] = values.appointment_date;
          params['task']['appointment_time'] = Utils.ConvertTime(values.appointment_time);
          data.formHandler(params);
        } else {
          params['task']['due_date'] = values.due_date;
          data.formHandler(params);
        }
      }}
    >
      {({ values, setFieldValue, errors, isSubmitting, touched, setTouched }) => {
        return (
          <Form>
            {data.selectedDateType == "appointment_date" && (
              <>
                <label>Appointment Date & Time</label>
                <div className="flex">
                  <div className={"flex datefield-wrapper"}>
                    <Field name={"date"}>
                      {({ field, meta }) => {
                        return (
                          <>
                            <BsFillCalendarEventFill className="calendar-icon" />
                            <ReactDatePicker
                              {...field}
                              className="datepicker"
                              dateFromat={"YYYY-MM-dd"}
                              autoComplete="off"
                              onChange={(date) => {
                                setFieldValue("appointment_date", date);
                                setFieldValue("date", date.toISOString().slice(0, 10));
                              }}
                            />
                          </>
                        );
                      }}
                    </Field>
                  </div>
                  <div className={"flex datefield-wrapper"}>
                    <BsClockFill className="calendar-icon" />
                    <input
                      name="appointment_time"
                      className="datepicker"
                      onChange={(e) => setFieldValue("appointment_time", e.target.value)}
                    />
                  </div>
                </div>
                <div className={"row"}>
                  <div className={"col-md-6"}>
                    {!values.appointment_date && errors.appointment_date ? (
                      <FieldError error={errors.appointment_date} />
                    ) : null}
                  </div>
                  <div className={"col-md-6"}>
                    {!values.appointment_time && errors.appointment_time ? (
                      <FieldError error={errors.appointment_time} />
                    ) : null}
                  </div>
                </div>
              </>
            )}
            {data.selectedDateType == "due_date" && (
              <>
                <label>Due Date</label>
                <div className={"flex duedate-wrapper"}>
                  <Field name={"date"}>
                    {({ field, meta }) => {
                      return (
                        <>
                          <BsFillCalendarEventFill className="calendar-icon" />
                          <ReactDatePicker
                            {...field}
                            className="datepicker"
                            dateFromat={"YYYY-MM-dd"}
                            onChange={(date) => {
                              setFieldValue("due_date", date);
                              setFieldValue("date", date.toISOString().slice(0, 10));
                            }}
                          />
                        </>
                      );
                    }}
                  </Field>
                </div>

                {errors.due_date ? <FieldError error={errors.due_date} /> : null}
              </>
            )}

            {data.selectedDateType && (
              <>
                <label>{"Servicing Provider"}</label>
                {data.primary_code && !data.ooa ? (
                  <SelectPcp name="pcp" required={false} setFieldValue={setFieldValue} />
                ) : (
                  <input
                    className="default"
                    name="pcp"
                    value={values.pcp}
                    onChange={(e) => setFieldValue("pcp", e.target.value)}
                  />
                )}
                <FormGroup controlId="formFile" className="mb-3">
                  <label>
                    Order Document
                    <span id="from">
                      {data.order_document && data.order_document.url
                        ? " Choose file to replace order document"
                        : " Attach order document if applicable"}
                    </span>
                  </label>
                  <FormControl
                    type="file"
                    onChange={(e) => {
                      setFieldValue("order_document", e.target.files[0]);
                    }}
                  />
                  {data.order_document && data.order_document.url && (
                    <Figure className="document mt4">
                      <Link
                        to={{ pathname: data.order_document.url }}
                        target="_blank"
                        className="no-style"
                        rel="noopener noreferrer"
                      >
                        <b>
                          Order <IoMdOpen />
                        </b>
                        <p>{Utils.FileNameFromUrl(data.order_document.url)}</p>
                      </Link>
                    </Figure>
                  )}
                </FormGroup>
                <button className="check" type="submit" disabled={isSubmitting}>
                  Submit
                </button>
              </>
            )}
          </Form>
        );
      }}
    </Formik>
  );
});

const NoExtensionForm = React.memo(({ data }) => {
  const settings = useContext(Settings);
  return (
    <Formik
      initialValues={{
        pcp: data.servicing_provider ? data.servicing_provider : "",
        order_document: "",
      }}
      onSubmit={(values) => {
        const formData = new FormData();
        formData.append("task[id]", data.id);
        if (values.order_document) {
          formData.append("task[order_document]", values.order_document);
        } else {
          formData.append("task[status]", "closed");
        }
        formData.append("task[in_review]", true);

        if (data.responseId) {
          formData.append("task[response_attributes[outcome]]", "cpr_still_required");
          formData.append("task[response_attributes[outcome_set_by_id]]", settings.me.id);
          formData.append("task[response_attributes[id]]", data.responseId);
        }
        formData.append("task[assigner_id]", settings.me.id);
        formData.append("task[servicing_provider]", values.pcp);
        data.formHandler(formData);
      }}
    >
      {({ setFieldValue, isSubmitting, values, touched, setTouched }) => {
        return (
          <Form>
            <label>{"Servicing Provider"}</label>
            {data.primary_code && !data.ooa ? (
              <SelectPcp name="pcp" required={false} setFieldValue={setFieldValue} />
            ) : (
              <input
                className="default"
                name="pcp"
                value={values.pcp}
                onChange={(e) => setFieldValue("pcp", e.target.value)}
              />
            )}
            <FormGroup controlId="formFile" className="mb-3">
              <label>
                Order Document
                <span id="from">
                  {data.order_document && data.order_document.url
                    ? " Choose file to replace order document"
                    : " Attach order document if applicable"}
                </span>
              </label>
              <FormControl
                type="file"
                onChange={(e) => {
                  setFieldValue("order_document", e.target.files[0]);
                }}
              />
              {data.order_document && data.order_document.url && (
                <Figure className="document mt4">
                  <Link
                    to={{ pathname: data.order_document.url }}
                    target="_blank"
                    className="no-style"
                    rel="noopener noreferrer"
                  >
                    <b>
                      Order <IoMdOpen />
                    </b>
                    <p>{Utils.FileNameFromUrl(data.order_document.url)}</p>
                  </Link>
                </Figure>
              )}
            </FormGroup>
            <button className="check" type="submit" disabled={isSubmitting}>
              Submit
            </button>
          </Form>
        );
      }}
    </Formik>
  );
});

const Extension = React.memo(({ data }) => {
  const [extended, setExtended] = useState(null);
  const [extensionDateType, setExtensionDateType] = useState("");
  return (
    <div className="mt20">
      <b>Will the patient complete this CPR by the following</b>
      <div className="mtb10">
        <b>
          {data.date_type}:
          <BsFillCalendarEventFill className="cpr-label-icon" />
          {data.date_type == "Appointment Date" ? data.appointment_date : data.due_date}
          {data.appointment_time && (
            <>
              <BsClockFill className="cpr-label-icon" />
              {new Date(data.appointment_time).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              ({Intl.DateTimeFormat().resolvedOptions().timeZone})
            </>
          )}
        </b>
      </div>
      <select
        onChange={(e) => {
          e.target.value == "true" ? setExtended(true) : setExtended(false);
        }}
      >
        <option value={""}>{"Select an option"}</option>
        <option value={"false"}>{"Yes, patient will complete this CPR " + data.date_type}</option>
        <option value={"true"}> {"No, patient needs an extension"}</option>
      </select>
      <div className="mt20">
        {extended == false && <NoExtensionForm data={data} />}
        {extended && (
          <>
            <b>Would you like to provide a new Due Date or Appointment Date?</b>
            <select
              className="cpr-form"
              onChange={(e) => {
                setExtensionDateType(e.target.value);
              }}
            >
              <option value={""}>{"Select a date type"}</option>
              <option value={"due_date"}>{"Due Date"}</option>
              <option value={"appointment_date"}> {"Apppointment Date"}</option>
            </select>
            <ExtendedForm data={{ ...data, selectedDateType: extensionDateType, id: data.id }} />
          </>
        )}
      </div>
    </div>
  );
});

export default Extension;
